/** Pure helpers for the Visitors Desk analyst UI (Summary page, chat, cards). */

export type AgentGroup = 'all' | 'nonmember' | 'lapsed' | 'member'
export type Direction = 'up' | 'flat' | 'down'
export type Goal = 'convert' | 'win_back' | 'retain' | 'overall'

export function directionLabel(d: Direction): string {
  return d === 'up' ? 'Growing' : d === 'down' ? 'Shrinking' : 'Flat'
}

export function groupLabel(g: string): string {
  switch (g) {
    case 'all': return 'Everyone'
    case 'nonmember': return 'Non-members'
    case 'lapsed': return 'Lapsed members'
    case 'member': return 'Members'
    default: return g
  }
}

export function goalLabel(goal: string): string {
  switch (goal) {
    case 'convert': return 'Convert'
    case 'win_back': return 'Win back'
    case 'retain': return 'Retain'
    default: return 'Overall'
  }
}

export function isPending(status: string | null | undefined): boolean {
  return status === 'queued' || status === 'running'
}

/** A queued/running run older than this (since it started, else since it was queued) is given up on. */
export const STALE_RUN_MS = 15 * 60 * 1000

/** True while a run is queued or running and not yet past the 15-minute cutoff, so the page keeps polling. */
export function isLivePending(
  run: { status?: string | null; created_at?: string | null; started_at?: string | null } | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!run || !isPending(run.status)) return false
  const at = Date.parse(run.started_at ?? run.created_at ?? '')
  if (Number.isNaN(at)) return true
  return now - at < STALE_RUN_MS
}

/** A run still queued/running past the cutoff: the worker is busy or off. */
export function isStalePending(
  run: { status?: string | null; created_at?: string | null; started_at?: string | null } | null | undefined,
  now: number = Date.now(),
): boolean {
  return !!run && isPending(run.status) && !isLivePending(run, now)
}

export const STALE_RUN_TEXT = "The analyst hasn't finished — the worker may be busy or off. Try again later."

export interface HandoffRec { handoff_kind: string | null; handoff_available: boolean; handed_off_at: string | null }

const DESK: Record<string, string> = { campaign: 'Campaign Desk', editorial: 'Editorial Desk' }

export function handoffButton(rec: HandoffRec, sentNow = false): { label: string; disabled: boolean } | null {
  const desk = rec.handoff_kind ? DESK[rec.handoff_kind] : undefined
  if (!desk) return null
  if (rec.handed_off_at || sentNow) return { label: `Sent to ${desk} ↗`, disabled: true }
  if (!rec.handoff_available) return null
  return { label: `Send to ${desk}`, disabled: false }
}

/** Only internal desk links are followed; anything else is dropped. */
export function safeDeskHref(href: string | null | undefined): string | null {
  if (typeof href !== 'string') return null
  if (!href.startsWith('/visitors/desk/') || href.startsWith('//') || /[\s\\]/.test(href)) return null
  return href
}

export interface MdSeg { text: string; bold: boolean }
export interface MdBlock { kind: 'p' | 'li'; segs: MdSeg[] }

function inline(line: string): MdSeg[] {
  const out: MdSeg[] = []
  const parts = line.split('**')
  parts.forEach((t, i) => {
    if (t === '') return
    // An unmatched trailing ** stays plain text.
    const unmatched = i % 2 === 1 && i === parts.length - 1
    out.push(unmatched ? { text: `**${t}`, bold: false } : { text: t, bold: i % 2 === 1 })
  })
  return out
}

/** Paragraphs, "- " bullets and **bold** only. The result is data for React text nodes, never HTML. */
export function mdBlocks(text: string): MdBlock[] {
  const blocks: MdBlock[] = []
  let para: string[] = []
  const flush = () => {
    if (para.length) blocks.push({ kind: 'p', segs: inline(para.join(' ')) })
    para = []
  }
  for (const raw of (text ?? '').replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim()
    if (!line) { flush(); continue }
    const m = /^[-*]\s+(.*)$/.exec(line)
    if (m) { flush(); blocks.push({ kind: 'li', segs: inline(m[1]) }) } else para.push(line)
  }
  flush()
  return blocks
}
